import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  BookHeart, Loader2, Check, X, Eye, Trash2, Star, Globe,
  Clock, User, Search, Filter, ChevronDown, ChevronUp, Edit,
  Plus, FileText, Image
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import SeoMetaEditor from "./SeoMetaEditor";
import ViewLiveLink from "./ViewLiveLink";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Badge } from "../ui/badge";

const MEMORY_CATEGORIES = {
  upton_park: "Upton Park/Boleyn Ground",
  european: "European Nights",
  world_cup: "1966 World Cup",
  away_days: "Away Days",
  derby: "Derby Days",
  legends: "Club Legends",
  general: "General Memories"
};

const FanMemoriesAdmin = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("submissions");
  
  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [submissionFilter, setSubmissionFilter] = useState("pending");
  
  // Published memories state
  const [memories, setMemories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "general",
    author_name: "",
    image_url: "",
    is_featured: false
  });

  useEffect(() => {
    if (activeTab === "submissions") {
      fetchSubmissions();
    } else {
      fetchMemories();
    }
  }, [activeTab, submissionFilter, categoryFilter]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const url = submissionFilter === "all" 
        ? `${API}/admin/fan-memories/submissions`
        : `${API}/admin/fan-memories/submissions?status=${submissionFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data.submissions || []);
      setSubmissionCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/fan-memories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemories(response.data.memories || []);
    } catch (error) {
      toast.error("Failed to load memories");
    } finally {
      setLoading(false);
    }
  };

  const approveSubmission = async (submissionId) => {
    try {
      await axios.post(`${API}/admin/fan-memories/submissions/${submissionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Memory approved and published!");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectSubmission = async () => {
    if (!selectedSubmission) return;
    try {
      await axios.post(`${API}/admin/fan-memories/submissions/${selectedSubmission.id}/reject`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: rejectReason || null }
      });
      toast.success("Submission rejected");
      setShowRejectModal(false);
      setRejectReason("");
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/admin/fan-memories/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Submission deleted");
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const createMemory = async () => {
    try {
      await axios.post(`${API}/admin/fan-memories`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Memory created!");
      setShowCreateModal(false);
      resetForm();
      fetchMemories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create memory");
    }
  };

  const updateMemory = async () => {
    if (!selectedMemory) return;
    try {
      await axios.put(`${API}/admin/fan-memories/${selectedMemory.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Memory updated!");
      setShowEditModal(false);
      resetForm();
      fetchMemories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const deleteMemory = async (memoryId) => {
    if (!confirm("Delete this memory permanently?")) return;
    try {
      await axios.delete(`${API}/admin/fan-memories/${memoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Memory deleted");
      fetchMemories();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleFeatured = async (memoryId) => {
    try {
      await axios.post(`${API}/admin/fan-memories/${memoryId}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMemories();
    } catch (error) {
      toast.error("Failed to toggle featured status");
    }
  };

  const togglePublished = async (memoryId) => {
    try {
      await axios.post(`${API}/admin/fan-memories/${memoryId}/toggle-published`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMemories();
    } catch (error) {
      toast.error("Failed to toggle published status");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      category: "general",
      author_name: "",
      image_url: "",
      is_featured: false
    });
    setSelectedMemory(null);
  };

  const openEditModal = (memory) => {
    setSelectedMemory(memory);
    setFormData({
      title: memory.title,
      content: memory.content,
      category: memory.category,
      author_name: memory.author_name,
      image_url: memory.image_url || "",
      is_featured: memory.is_featured || false
    });
    setShowEditModal(true);
  };

  const filteredMemories = memories.filter(m => {
    if (categoryFilter !== "all" && m.category !== categoryFilter) return false;
    if (searchQuery && !m.title?.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !m.author_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading && submissions.length === 0 && memories.length === 0) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="fan-memories-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <BookHeart className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          FAN MEMORIES ADMIN
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-600" />
            <div>
              <div className="text-2xl font-bold text-amber-800">{submissionCounts.pending}</div>
              <div className="text-xs text-amber-600">Pending Review</div>
            </div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Check className="w-8 h-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold text-green-800">{submissionCounts.approved}</div>
              <div className="text-xs text-green-600">Approved</div>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <X className="w-8 h-8 text-red-600" />
            <div>
              <div className="text-2xl font-bold text-red-800">{submissionCounts.rejected}</div>
              <div className="text-xs text-red-600">Rejected</div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-blue-800">{memories.length}</div>
              <div className="text-xs text-blue-600">Published</div>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="submissions" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
            <Clock className="w-4 h-4 mr-2" />Submissions ({submissionCounts.pending})
          </TabsTrigger>
          <TabsTrigger value="published" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
            <Globe className="w-4 h-4 mr-2" />Published Memories
          </TabsTrigger>
        </TabsList>

        {/* Submissions Tab */}
        <TabsContent value="submissions">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            {/* Filter buttons */}
            <div className="flex gap-2 mb-4">
              {["pending", "approved", "rejected", "all"].map(status => (
                <Button
                  key={status}
                  variant={submissionFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSubmissionFilter(status)}
                  className={submissionFilter === status ? "bg-[#7C2C3B]" : ""}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>

            {submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No {submissionFilter !== "all" ? submissionFilter : ""} submissions</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map(submission => (
                  <div key={submission.id} className="border border-slate-200 rounded-lg p-4 hover:border-[#7C2C3B]/30 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-[#0F172A]">{submission.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <User className="w-4 h-4" />
                          <span>{submission.submitted_by_name}</span>
                          <span>•</span>
                          <span>{submission.submitted_by_email}</span>
                          <span>•</span>
                          <span>{formatDate(submission.created_at)}</span>
                        </div>
                      </div>
                      <Badge className={
                        submission.status === "pending" ? "bg-amber-500" :
                        submission.status === "approved" ? "bg-green-500" : "bg-red-500"
                      }>
                        {submission.status}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline">{MEMORY_CATEGORIES[submission.category] || submission.category}</Badge>
                      {submission.image_url && (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          <Image className="w-3 h-3 mr-1" />Has Image
                        </Badge>
                      )}
                    </div>

                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">{submission.content}</p>

                    {submission.admin_notes && (
                      <div className="bg-slate-50 rounded p-2 mb-3 text-sm">
                        <span className="font-medium">Admin notes:</span> {submission.admin_notes}
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setSelectedSubmission(submission); setShowPreviewModal(true); }}
                      >
                        <Eye className="w-4 h-4 mr-1" />Preview
                      </Button>

                      {submission.status === "pending" && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => approveSubmission(submission.id)}
                            data-testid={`approve-${submission.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => { setSelectedSubmission(submission); setShowRejectModal(true); }}
                            data-testid={`reject-${submission.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />Reject
                          </Button>
                        </div>
                      )}

                      {submission.status !== "pending" && (
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => deleteSubmission(submission.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Published Memories Tab */}
        <TabsContent value="published">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search memories..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(MEMORY_CATEGORIES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="bg-[#7C2C3B] hover:bg-[#5a1f2b]"
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                data-testid="create-memory-btn"
              >
                <Plus className="w-4 h-4 mr-2" />Add Memory
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Featured</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMemories.map(memory => (
                    <TableRow key={memory.id}>
                      <TableCell>
                        {memory.image_url && (
                          <img 
                            src={memory.image_url} 
                            alt="" 
                            className="w-8 h-8 rounded object-cover"
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{memory.title}</TableCell>
                      <TableCell>{memory.author_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{MEMORY_CATEGORIES[memory.category] || memory.category}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePublished(memory.id)}
                          className={memory.is_published ? "text-green-600" : "text-slate-400"}
                        >
                          {memory.is_published ? <Eye className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFeatured(memory.id)}
                          className={memory.is_featured ? "text-amber-500" : "text-slate-300"}
                        >
                          <Star className={`w-4 h-4 ${memory.is_featured ? "fill-amber-500" : ""}`} />
                        </Button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(memory.published_at || memory.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <ViewLiveLink slug={memory.slug} path="/fan-memories" testId={`memory-${memory.id}`} />
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(memory)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteMemory(memory.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {filteredMemories.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No memories found</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedSubmission?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <User className="w-4 h-4" />
              <span>{selectedSubmission?.submitted_by_name}</span>
              <span>({selectedSubmission?.submitted_by_email})</span>
            </div>
            <Badge variant="outline">{MEMORY_CATEGORIES[selectedSubmission?.category] || selectedSubmission?.category}</Badge>
            {selectedSubmission?.image_url && (
              <img src={selectedSubmission.image_url} alt="" className="w-full max-h-64 object-cover rounded-lg" />
            )}
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap">{selectedSubmission?.content}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">Are you sure you want to reject "{selectedSubmission?.title}"?</p>
            <Textarea
              placeholder="Reason for rejection (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={rejectSubmission}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => { 
        if (!open) { setShowCreateModal(false); setShowEditModal(false); resetForm(); }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{showEditModal ? "Edit Memory" : "Create New Memory"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Memory title"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Author Name</label>
              <Input 
                value={formData.author_name}
                onChange={(e) => setFormData({...formData, author_name: e.target.value})}
                placeholder="Author's name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MEMORY_CATEGORIES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Content</label>
              <Textarea 
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Memory content..."
                rows={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Image URL (optional)</label>
              <Input 
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="is_featured" className="text-sm">Featured Memory</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}>Cancel</Button>
            <Button 
              className="bg-[#7C2C3B]" 
              onClick={showEditModal ? updateMemory : createMemory}
              data-testid="save-memory-btn"
            >
              {showEditModal ? "Update" : "Create"}
            </Button>
          </DialogFooter>
          {/* SEO metadata editor — only shown on Edit (needs an existing id) */}
          {showEditModal && selectedMemory?.id && (
            <div className="px-1 pb-2">
              <SeoMetaEditor collection="fan_memories" docId={selectedMemory.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FanMemoriesAdmin;
